import type { RequestConfig } from '../types';

/** Cliente HTTP mínimo (get). */
export interface LeaderboardApiClient {
  get<T>(url: string, config?: RequestConfig): Promise<T>;
}

/** Item retornado por GET v1/LeaderBoardDashboard/pagination */
export interface LeaderboardDashboardApiItem {
  rank: number;
  name: string;
  email: string;
  totalPoints: number;
  coursesCompleted: number;
  lastAccessDate: string;
}

/** Corpo JSON da API (text/plain ou application/json). */
export interface LeaderboardDashboardPaginationResponse {
  items: LeaderboardDashboardApiItem[];
  totalRecords: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
}

export interface FetchLeaderboardDashboardParams {
  page: number;
  limit: number;
  search?: string;
}

export interface FetchLeaderboardDashboardResult {
  data: LeaderboardDashboardApiItem[];
  total: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
}

const LEADERBOARD_PAGINATION_PATH = 'v1/LeaderBoardDashboard/pagination';

function normalizeResponse(
  raw: LeaderboardDashboardPaginationResponse
): FetchLeaderboardDashboardResult {
  const items = Array.isArray(raw.items) ? raw.items : [];
  return {
    data: items,
    total: typeof raw.totalRecords === 'number' ? raw.totalRecords : items.length,
    totalPages: typeof raw.totalPages === 'number' ? raw.totalPages : 1,
    pageNumber: typeof raw.pageNumber === 'number' ? raw.pageNumber : 1,
    pageSize: typeof raw.pageSize === 'number' ? raw.pageSize : items.length,
  };
}

/**
 * Leaderboard admin — GET v1/LeaderBoardDashboard/pagination
 * Query: pageNumber, pageSize, SearchText (opcional).
 */
export function createLeaderboardApi(api: LeaderboardApiClient) {
  return {
    async fetchLeaderboardDashboard(
      params: FetchLeaderboardDashboardParams
    ): Promise<FetchLeaderboardDashboardResult> {
      const { page, limit, search } = params;
      const searchParams = new URLSearchParams();
      searchParams.set('pageNumber', String(page));
      searchParams.set('pageSize', String(limit));
      if (search && search.trim()) {
        searchParams.set('SearchText', search.trim());
      }

      const query = searchParams.toString();
      const url = `/${LEADERBOARD_PAGINATION_PATH}?${query}`;

      try {
        const raw = await api.get<LeaderboardDashboardPaginationResponse>(url, {
          headers: {
            Accept: 'text/plain',
          },
        });
        return normalizeResponse(raw);
      } catch {
        return {
          data: [],
          total: 0,
          totalPages: 0,
          pageNumber: page,
          pageSize: limit,
        };
      }
    },
  };
}

export type LeaderboardApi = ReturnType<typeof createLeaderboardApi>;
